// Wave-D harness — steering path-scoped (consenso triplo Cursor rules
// alwaysApply/globs · Claude Code .claude/rules paths: · Codex AGENTS.md em
// camadas — ver 04-DOCS-VENDORS.md). Um arquivo de .apollo/steering pode
// declarar na 1ª linha útil `paths: apolo/src/**/*.jsx, **/*.css` — a regra só
// entra no prompt quando o TURNO toca um path que casa (v1: paths extraídos do
// texto da tarefa; regra sem `paths:` = alwaysApply, comportamento idêntico ao
// atual). Puro, sem dependência de glob externa.

import { matchesGlob } from './globMatch.js';
import { resolveActivation } from './context/ruleActivation.js';

const PATHS_RE = /^(?:<!--\s*)?paths:\s*(.+?)\s*(?:-->)?\s*$/i;
const TRIGGER_RE = /^(?:<!--\s*)?trigger:\s*(\w+)\s*(?:-->)?\s*$/i;

/** harness-uplift-r2 E3 — extrai a diretiva `trigger:` (Cascade) das primeiras
 *  linhas úteis; devolve o trigger + content sem a diretiva. Puro. */
export function parseTriggerDirective(content) {
  const text = String(content ?? '');
  const lines = text.split('\n');
  for (let i = 0; i < Math.min(lines.length, 6); i += 1) {
    const line = lines[i].trim();
    if (!line || line === '---') continue;
    const m = line.match(TRIGGER_RE);
    if (!m) continue;
    const rest = [...lines.slice(0, i), ...lines.slice(i + 1)].join('\n');
    return { trigger: m[1].toLowerCase(), content: rest };
  }
  return { trigger: null, content: text };
}

/** E3 — resolve UMA fonte que declara `trigger:` pelo resolver de tiers.
 *  Retorna a entrada a incluir, ou null (não-ativa). Puro. */
function activateTieredSource(src, paths, mentioned) {
  const { trigger, content } = parseTriggerDirective(src?.content);
  if (!trigger) return undefined; // sem trigger → deixa o caller usar o path legado
  const { globs } = parseScopeDirective(content);
  const res = resolveActivation(
    { trigger, name: src?.name, description: src?.description, content, globs, scope: src?.scope },
    { paths, mentioned },
  );
  return res.include === 'none' ? null : { ...src, content: res.content };
}

/** Extrai a diretiva `paths:` das primeiras linhas úteis; devolve globs + content sem a diretiva. */
export function parseScopeDirective(content) {
  const text = String(content ?? '');
  const lines = text.split('\n');
  for (let i = 0; i < Math.min(lines.length, 5); i += 1) {
    const line = lines[i].trim();
    if (!line || line === '---') continue; // tolera frontmatter/linhas vazias no topo
    const m = line.match(PATHS_RE);
    if (!m) return { globs: null, content: text };
    const globs = m[1].split(',').map((g) => g.trim()).filter(Boolean);
    const rest = [...lines.slice(0, i), ...lines.slice(i + 1)].join('\n');
    return { globs: globs.length ? globs : null, content: rest };
  }
  return { globs: null, content: text };
}

// matchesGlob was extracted to globMatch.js to break the steeringScope↔ruleActivation
// cycle (E3). Re-exported here so existing importers keep working unchanged.
export { matchesGlob };

/** Paths plausíveis mencionados no texto da tarefa (v1 do "arquivos em contexto"). */
export function extractPathsFromText(text) {
  const found = String(text ?? '').match(/[\w./-]+\.[a-z]{1,6}(?=[\s,;:)\]'"`]|$)/gi) || [];
  return [...new Set(found.map((p) => p.replace(/^\.\//, '')))];
}

/**
 * Regra sem `paths:` passa SEMPRE (alwaysApply). Com `paths:`: entra só quando
 * algum path em contexto casa. A diretiva é removida do content enviado.
 */
/** Legacy path-scope decision for ONE source (byte-identical pre-E3). Pure. */
function activateByScope(src, paths) {
  const { globs, content } = parseScopeDirective(src?.content);
  if (!globs) return src; // sem paths: → alwaysApply
  const active = paths.some((p) => globs.some((g) => matchesGlob(p, g)));
  return active ? { ...src, content } : null;
}

/** Decide inclusion for ONE source: E3 tiers first (when on), else legacy scope.
 *  Returns the entry to include, or null to drop. Keeps the loop flat. */
function resolveSourceInclusion(src, paths, mentioned, tiers) {
  if (tiers) {
    const tiered = activateTieredSource(src, paths, mentioned);
    if (tiered !== undefined) return tiered; // trigger declared → resolver decides (entry|null)
  }
  return activateByScope(src, paths); // no trigger / tiers off → legacy
}

export function filterSteeringByScope(sources, contextPaths = [], opts = {}) {
  if (!Array.isArray(sources) || sources.length === 0) return [];
  const paths = Array.isArray(contextPaths) ? contextPaths : [];
  const tiers = opts && opts.tiersEnabled === true;
  const mentioned = Array.isArray(opts?.mentioned) ? opts.mentioned : [];
  const out = [];
  for (const src of sources) {
    const entry = resolveSourceInclusion(src, paths, mentioned, tiers);
    if (entry) out.push(entry);
  }
  return out;
}
